// ============================================================
// Chess Arena — Piece (Phaser Container)
// ============================================================

import Phaser from 'phaser';
import {
  GridPos,
  PieceState,
  PieceType,
  Team,
  PIECE_CONFIGS,
} from './types';
import { Board } from './board';
import eventBus from './eventBus';

/** Movement speed in cells per second for sliding pieces. */
const SLIDE_SPEED = 1.725;
/** Fixed duration (seconds) for knight arc jumps. */
const KNIGHT_JUMP_DURATION = 1.304;
/** Alpha for pieces hidden in a bush (own team). */
const BUSH_ALPHA = 0.5;
/** Cooldown overlay size (matches piece sprite). */
const CD_OVERLAY_SIZE = 80; // will be scaled with piece

export class Piece extends Phaser.GameObjects.Container {
  public pieceState: PieceState;
  public sprite: Phaser.GameObjects.Image;
  public cooldownBar: Phaser.GameObjects.Graphics;
  public cooldownIcon: Phaser.GameObjects.Image;

  private cooldownTotal: number = 0;
  /** Full cell size for cooldown overlay — set by PieceManager after creation. */
  public cellSize: number = CD_OVERLAY_SIZE;
  private premoveIndicator: Phaser.GameObjects.Graphics | null = null;
  private premoveIndicatorTween: Phaser.Tweens.Tween | null = null;
  private premoveGhost: Phaser.GameObjects.Image | null = null;
  private checkIndicator: Phaser.GameObjects.Text | null = null;
  private checkIndicatorTween: Phaser.Tweens.Tween | null = null;
  private _isInCheck: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    state: PieceState,
    spriteKey: string,
  ) {
    super(scene, x, y);

    this.pieceState = state;

    // --- Cooldown background (behind the piece so it doesn't occlude) ---
    this.cooldownBar = scene.add.graphics();
    this.cooldownBar.setVisible(false);
    this.add(this.cooldownBar);

    // --- Piece sprite (on top of cooldown bar) ---
    this.sprite = scene.add.image(0, 0, spriteKey);
    this.add(this.sprite);

    // --- Cooldown clock icon (small, bottom-right corner) ---
    const clockKey = scene.textures.exists('icon_clock') ? 'icon_clock' : '__DEFAULT';
    this.cooldownIcon = scene.add.image(0, 0, clockKey);
    this.cooldownIcon.setDisplaySize(28, 28);
    this.cooldownIcon.setAlpha(0.85);
    this.cooldownIcon.setVisible(false);
    this.add(this.cooldownIcon);

    // --- Premove indicator (gold arrow graphic, created lazily) ---
    this.premoveIndicator = null;

    this.setSize(this.sprite.width, this.sprite.height);
    scene.add.existing(this);
  }

  // ------------------------------------------------------------------
  // Cooldown
  // ------------------------------------------------------------------

  /** Start a cooldown period. */
  startCooldown(duration: number): void {
    this.pieceState.cooldownRemaining = duration;
    this.cooldownTotal = duration;
    this.cooldownBar.setVisible(true);
    this.cooldownIcon.setVisible(true);
    this.drawCooldownBar();
  }

  /** Update cooldown each frame. Called by the scene update loop.
   * @param delta Frame delta in milliseconds.
   */
  updateCooldown(delta: number): void {
    if (this.pieceState.cooldownRemaining <= 0) return;

    this.pieceState.cooldownRemaining -= delta / 1000;

    if (this.pieceState.cooldownRemaining <= 0) {
      this.pieceState.cooldownRemaining = 0;
      this.cooldownBar.setVisible(false);
      this.cooldownIcon.setVisible(false);

      if (!this.pieceState.isInBush) {
        this.sprite.setAlpha(1);
      }

      eventBus.emit('cooldown_end', { pieceId: this.pieceState.id });

      // Execute premove if queued
      console.log('[DEBUG] Cooldown ended for', this.pieceState.id, 'premove=', this.pieceState.premove);
      if (this.pieceState.premove) {
        console.log('[DEBUG] Emitting premove_executed for', this.pieceState.id, 'target=', JSON.stringify(this.pieceState.premove));
        eventBus.emit('premove_executed', {
          pieceId: this.pieceState.id,
          target: this.pieceState.premove,
        });
      }
    } else {
      this.drawCooldownBar();
    }
  }

  /** Whether cooldown is currently active. */
  isCooldownActive(): boolean {
    return this.pieceState.cooldownRemaining > 0;
  }

  private drawCooldownBar(): void {
    this.cooldownBar.clear();
    const remaining = this.pieceState.cooldownRemaining / this.cooldownTotal;
    const overlaySize = this.cellSize;
    const half = overlaySize / 2;

    // Subtle background tint (behind the piece sprite)
    this.cooldownBar.fillStyle(0x000000, 0.25);
    this.cooldownBar.fillRoundedRect(-half, -half, overlaySize, overlaySize, 8);

    // Bottom-up colored fill showing remaining cooldown (behind piece)
    const fillH = overlaySize * remaining;
    if (fillH > 0) {
      this.cooldownBar.fillStyle(0x000000, 0.35);
      this.cooldownBar.fillRect(-half, half - fillH, overlaySize, fillH);
    }

    // Border ring that shrinks with cooldown — visible around the piece
    const borderColor = this.pieceState.team === 'player' ? 0x4488ff : 0xff4444;
    this.cooldownBar.lineStyle(3, borderColor, 0.6);
    this.cooldownBar.strokeRoundedRect(-half, -half, overlaySize, overlaySize, 8);

    // Position clock icon in bottom-right corner
    this.cooldownIcon.setPosition(half - 16, half - 16);
  }

  // ------------------------------------------------------------------
  // Movement
  // ------------------------------------------------------------------

  /**
   * Start a tween-based move to the target grid position.
   * @param targetPixel Target pixel coordinates { x, y }.
   * @param cellSize Size of a single cell (for speed calculation).
   * @param isKnight Whether to use the knight arc animation.
   */
  startMove(
    targetPixel: { x: number; y: number },
    cellSize: number,
    isKnight: boolean,
  ): void {
    this.pieceState.isMoving = true;
    this.pieceState.moveStartTime = Date.now();
    // Clear premove indicator immediately when movement starts
    this.clearPremoveIndicator();

    if (isKnight) {
      // Knight: arc jump (fixed 0.3s) using quadratic Bezier
      const startX = this.x;
      const startY = this.y;
      const midX = (startX + targetPixel.x) / 2;
      const midY = Math.min(startY, targetPixel.y) - cellSize * 0.8;
      const durationMs = KNIGHT_JUMP_DURATION * 1000;

      this.scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: durationMs,
        ease: 'Sine.easeInOut',
        onUpdate: (tween) => {
          const t = tween.getValue() ?? 0;
          const oneMinusT = 1 - t;
          this.x = oneMinusT * oneMinusT * startX
            + 2 * oneMinusT * t * midX
            + t * t * targetPixel.x;
          this.y = oneMinusT * oneMinusT * startY
            + 2 * oneMinusT * t * midY
            + t * t * targetPixel.y;
        },
        onComplete: () => this.onMoveComplete(),
      });

      // Safety net: if the addCounter onComplete doesn't fire, ensure
      // onMoveComplete runs after the animation duration + small buffer.
      this.scene.time.delayedCall(durationMs + 50, () => {
        if (this.pieceState.isMoving) {
          this.x = targetPixel.x;
          this.y = targetPixel.y;
          this.onMoveComplete();
        }
      });
    } else {
      // Sliding: constant speed (6 cells/sec)
      const dx = targetPixel.x - this.x;
      const dy = targetPixel.y - this.y;
      const distPx = Math.sqrt(dx * dx + dy * dy);
      const distCells = distPx / cellSize;
      const durationMs = (distCells / SLIDE_SPEED) * 1000;

      this.scene.tweens.add({
        targets: this,
        x: targetPixel.x,
        y: targetPixel.y,
        duration: durationMs,
        ease: 'Linear',
        onComplete: () => this.onMoveComplete(),
      });
    }
  }

  private onMoveComplete(): void {
    if (!this.scene) return;
    this.pieceState.isMoving = false;
    const config = PIECE_CONFIGS[this.pieceState.type];
    this.startCooldown(config.cooldown);
    this.clearPremoveIndicator();

    eventBus.emit('piece_moved_complete', {
      pieceId: this.pieceState.id,
      pos: this.pieceState.pos,
    });
  }

  // ------------------------------------------------------------------
  // Premove
  // ------------------------------------------------------------------

  /** Set or clear the premove target. */
  setPremove(target: GridPos | null): void {
    this.pieceState.premove = target;
    if (target) {
      this.drawPremoveIndicator(target);
    } else {
      this.clearPremoveIndicator();
    }
  }

  private drawPremoveIndicator(target: GridPos): void {
    this.clearPremoveIndicator();

    const cellSize = this.sprite.displayWidth / 0.8; // sprite is 80% of cell

    // Compute world-space positions for source and target
    const srcX = this.x;
    const srcY = this.y;
    const targetX = this.x + (target.col - this.pieceState.pos.col) * cellSize;
    const targetY = this.y + (target.row - this.pieceState.pos.row) * cellSize;

    // Use scene-level graphics at depth 202 so it shows above tutorial overlay (200)
    this.premoveIndicator = this.scene.add.graphics();
    this.premoveIndicator.setDepth(202);

    const half = cellSize / 2;

    // Gold filled cell at target (semi-transparent)
    this.premoveIndicator.fillStyle(0xffd700, 0.25);
    this.premoveIndicator.fillRect(targetX - half, targetY - half, cellSize, cellSize);

    // Gold border on target cell
    this.premoveIndicator.lineStyle(3, 0xffd700, 0.85);
    this.premoveIndicator.strokeRect(targetX - half, targetY - half, cellSize, cellSize);

    // Ghost piece sprite at target — shows where the piece will go
    const spriteKey = this.sprite.texture.key;
    if (this.scene.textures.exists(spriteKey)) {
      this.premoveGhost = this.scene.add.image(targetX, targetY, spriteKey);
      this.premoveGhost.setDisplaySize(cellSize * 0.7, cellSize * 0.7);
      this.premoveGhost.setAlpha(0.4);
      this.premoveGhost.setDepth(202);
    }

    // Dashed line from piece center to target center (world space)
    this.drawDashedLine(this.premoveIndicator, srcX, srcY, targetX, targetY, 0xffd700, 0.7, 3, 8, 6);

    // Arrow head at target end
    const angle = Math.atan2(targetY - srcY, targetX - srcX);
    const arrowLen = cellSize * 0.2;
    const arrowAngle = Math.PI / 6;
    this.premoveIndicator.lineStyle(3, 0xffd700, 0.7);
    this.premoveIndicator.beginPath();
    this.premoveIndicator.moveTo(
      targetX - arrowLen * Math.cos(angle - arrowAngle),
      targetY - arrowLen * Math.sin(angle - arrowAngle),
    );
    this.premoveIndicator.lineTo(targetX, targetY);
    this.premoveIndicator.lineTo(
      targetX - arrowLen * Math.cos(angle + arrowAngle),
      targetY - arrowLen * Math.sin(angle + arrowAngle),
    );
    this.premoveIndicator.strokePath();

    // Pulse animation on the indicator group
    const pulseTargets: Phaser.GameObjects.GameObject[] = [this.premoveIndicator];
    if (this.premoveGhost) pulseTargets.push(this.premoveGhost);
    this.premoveIndicatorTween = this.scene.tweens.add({
      targets: pulseTargets,
      alpha: { from: 1, to: 0.35 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Draw a dashed line on a Graphics object. */
  private drawDashedLine(
    gfx: Phaser.GameObjects.Graphics,
    x1: number, y1: number, x2: number, y2: number,
    color: number, alpha: number, lineWidth: number,
    dashLen: number, gapLen: number,
  ): void {
    const totalDx = x2 - x1;
    const totalDy = y2 - y1;
    const dist = Math.sqrt(totalDx * totalDx + totalDy * totalDy);
    if (dist === 0) return;
    const ux = totalDx / dist;
    const uy = totalDy / dist;

    gfx.lineStyle(lineWidth, color, alpha);
    let traveled = 0;
    while (traveled < dist) {
      const segEnd = Math.min(traveled + dashLen, dist);
      gfx.beginPath();
      gfx.moveTo(x1 + ux * traveled, y1 + uy * traveled);
      gfx.lineTo(x1 + ux * segEnd, y1 + uy * segEnd);
      gfx.strokePath();
      traveled = segEnd + gapLen;
    }
  }

  private clearPremoveIndicator(): void {
    if (this.premoveIndicatorTween) {
      this.premoveIndicatorTween.destroy();
      this.premoveIndicatorTween = null;
    }
    if (this.premoveIndicator) {
      this.premoveIndicator.destroy();
      this.premoveIndicator = null;
    }
    if (this.premoveGhost) {
      this.premoveGhost.destroy();
      this.premoveGhost = null;
    }
  }

  // ------------------------------------------------------------------
  // Check indicator (king in threat)
  // ------------------------------------------------------------------

  /** Show or hide the check warning indicator on this king. */
  setInCheck(inCheck: boolean): void {
    if (inCheck === this._isInCheck) return;
    this._isInCheck = inCheck;

    if (inCheck) {
      this.showCheckIndicator();
    } else {
      this.hideCheckIndicator();
    }
  }

  private showCheckIndicator(): void {
    this.hideCheckIndicator();

    // Red exclamation mark positioned at top-right of piece
    this.checkIndicator = this.scene.add.text(
      this.x + this.cellSize * 0.28,
      this.y - this.cellSize * 0.38,
      '!',
      {
        fontFamily: 'Arial Black, Arial, sans-serif',
        fontSize: '28px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#cc0000',
        strokeThickness: 6,
        align: 'center',
      },
    );
    this.checkIndicator.setOrigin(0.5, 0.5);
    this.checkIndicator.setDepth(12);

    // Pulse animation
    this.checkIndicatorTween = this.scene.tweens.add({
      targets: this.checkIndicator,
      scaleX: { from: 1, to: 1.3 },
      scaleY: { from: 1, to: 1.3 },
      alpha: { from: 1, to: 0.6 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private hideCheckIndicator(): void {
    if (this.checkIndicatorTween) {
      this.checkIndicatorTween.destroy();
      this.checkIndicatorTween = null;
    }
    if (this.checkIndicator) {
      this.checkIndicator.destroy();
      this.checkIndicator = null;
    }
  }

  /** Update check indicator position (call when piece moves). */
  updateCheckIndicatorPosition(): void {
    if (this.checkIndicator) {
      this.checkIndicator.setPosition(
        this.x + this.cellSize * 0.28,
        this.y - this.cellSize * 0.38,
      );
    }
  }

  // ------------------------------------------------------------------
  // Bush stealth
  // ------------------------------------------------------------------

  /** Set whether this piece is inside a bush. */
  setInBush(inBush: boolean): void {
    this.pieceState.isInBush = inBush;
    if (inBush && this.pieceState.team === 'player') {
      this.sprite.setAlpha(BUSH_ALPHA);
    } else if (inBush && this.pieceState.team === 'enemy') {
      // Enemy pieces in bushes are invisible to the player
      this.setVisible(false);
    } else {
      if (!this.isCooldownActive()) {
        this.sprite.setAlpha(1);
      }
      this.setVisible(true);
    }
  }

  // ------------------------------------------------------------------
  // Capture / death
  // ------------------------------------------------------------------

  /** Play a pop-in spawn animation (overshoot scale + flash). */
  playSpawnAnimation(): void {
    this.setScale(0);
    this.setAlpha(0);

    // Overshoot to 1.3x then settle to 1x
    this.scene.tweens.add({
      targets: this,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        // Quick punch: pop up then back down
        this.scene.tweens.add({
          targets: this,
          scaleX: 1.25,
          scaleY: 1.25,
          duration: 100,
          yoyo: true,
          ease: 'Quad.easeOut',
        });
      },
    });

    // Bright white flash ring behind the piece
    const ring = this.scene.add.graphics();
    ring.setDepth(this.depth - 1);
    ring.fillStyle(0xffffff, 0.8);
    ring.fillCircle(this.x, this.y, this.cellSize * 0.15);
    this.scene.tweens.add({
      targets: ring,
      alpha: 0,
      duration: 500,
      onUpdate: () => {
        const scale = 1 + (1 - ring.alpha) * 3; // expand as it fades
        ring.clear();
        ring.fillStyle(0xffffff, ring.alpha * 0.8);
        ring.fillCircle(this.x, this.y, this.cellSize * 0.15 * scale);
      },
      onComplete: () => ring.destroy(),
    });
  }

  /** Play capture animation then destroy the container. */
  capture(): void {
    this.pieceState.isAlive = false;

    // Quick death animation: scale down + fade out
    this.scene.tweens.add({
      targets: this,
      scaleX: 0,
      scaleY: 0,
      alpha: 0,
      duration: 250,
      ease: 'Back.easeIn',
      onComplete: () => {
        eventBus.emit('piece_captured', {
          pieceId: this.pieceState.id,
          type: this.pieceState.type,
          team: this.pieceState.team,
        });
        this.destroy();
      },
    });
  }
}
